import React, { useState, useMemo } from 'react';

import {
  FightIcon, FireIcon, GunIcon, PeopleIcon, BalaklavaIcon, ArrowInCircleRightIcon, BriefcaseIcon, LyingPersonIcon, HandsUpIcon,
  FallIcon,
} from '@vlabs/icons';
import { EmptyImage } from '@vlabs/shared/components/empty-image';
import { usePeopleCountThresholdInfo } from '@vlabs/shared/hooks';
import { routes } from '@vlabs/shared/navigation/routes';
import { calcSimilarityPercent } from '@vlabs/shared/utils';
import { CircleAnimatedButton } from '@vlabs/uikit';
import { useTranslation } from 'react-i18next';
import { generatePath, useHistory } from 'react-router-dom';

import st from './VideoAnalyticImageCard.module.sass';

const eventTypeConfig = {
  crowd: {
    icon: <PeopleIcon />,
    dataField: 'people_count',
  },
  fight: {
    icon: <FightIcon />,
    dataField: 'fight_score',
  },
  weapon: {
    icon: <GunIcon />,
    dataField: 'weapon_score',
  },
  fire: {
    icon: <FireIcon />,
    dataField: 'fire_score',
  },
  facecover: {
    icon: <BalaklavaIcon />,
    dataField: 'facecover_score',
  },
  bag: {
    icon: <BriefcaseIcon />,
  },
  lying: {
    icon: <LyingPersonIcon />,
    dataField: 'lying_score',
  },
  handsup: {
    icon: <HandsUpIcon />,
    dataField: 'handsup_score',
  },
  fall: {
    icon: <FallIcon />,
    dataField: 'fall_score',
  },
};

export function VideoAnalyticImageCard(vaEvent) {
  const { t } = useTranslation();
  const history = useHistory();
  const { label } = usePeopleCountThresholdInfo(vaEvent);
  const crowdThresholdLabel = label ? `(${label})` : '';

  const { event_type, event } = vaEvent;
  const [imageError, setImageError] = useState(false);
  const [isScoreVisible, setIsScoreVisible] = useState(false);
  const eventConfig = eventTypeConfig[event_type] || {};

  const score = useMemo(() => {
    if (!isScoreVisible || !event[eventConfig.dataField]) return null;
    return calcSimilarityPercent(event[eventConfig.dataField]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isScoreVisible, event[eventConfig.dataField]]);

  const hasImage = useMemo(() => {
    return event?.overview?.image && !imageError;
  }, [event, imageError]);

  if (!event || !event_type) return null;

  const onEventCardClick = (event_id) => {
    const eventLink = generatePath(routes.videoAnalytics.details, { eventId: event_id });
    history.push(eventLink);
  };

  return (
    <div className={st.VideoAnalyticCard}>
      <div className={st.GoToButton}>
        <CircleAnimatedButton
          icon={<ArrowInCircleRightIcon />}
          onClick={() => onEventCardClick(event.event_id)}
        >
          {t('events:page.title')}
        </CircleAnimatedButton>
      </div>
      <div className={st.ImageContainer}>
        {!!hasImage && (
        <React.Fragment>
          <div
            className={st.BackgroundImage}
            style={{ backgroundImage: `url(${event?.overview?.image})` }}
          />
          <img
            alt={t('videoAnalytics:archivePage.imageCard.alt.label')}
            className={st.MainImage}
            data-testid="fileRenderer.image"
            onError={() => setImageError(true)}
            src={event?.overview?.image}
          />
        </React.Fragment>
        )}
        {!hasImage && <EmptyImage className={st.MainImage} />}
      </div>

      <div
        className={st.InfoBlock}
        onMouseEnter={() => setIsScoreVisible(true)}
        onMouseLeave={() => setIsScoreVisible(false)}
      >
        <div
          className={st.InfoBlock__Icon}
          data-testid="InfoBlockIcon"
        >
          {eventConfig.icon}
        </div>

        {event_type === 'crowd' && (
          <div
            className={st.InfoBlock__Label}
            data-testid="VACardLabel"
          >
            {t('videoAnalytics:archivePage.imageCard.event_type.crowd.label', { count: event[eventConfig.dataField], defaultValue: '' })}
            {!!crowdThresholdLabel && <span className={st.InfoBlock__ThresholdLabel}>{crowdThresholdLabel}</span>}
          </div>
        )}

        {event_type !== 'crowd' && (
          <span
            className={st.InfoBlock__Label}
            data-testid="VACardLabel"
          >
            <span className={st.InfoBlock__Text}>
              {t(`videoAnalytics:archivePage.imageCard.event_type.${event_type}.label`)}
            </span>
            {!!score && <span className={st.InfoBlock__Score}>{score}</span>}
          </span>
        )}
      </div>
    </div>
  );
}
